<template>
  <div class="m-3" style="height: 90vh">
    <ReportsTable
      ref="rtable"
      :settings="{ hideTable: true, title: t('itemsInfoReport'), filterRequired: true, periodRequired: true, hideExport: true }"
      endpoint="reports/inventory/item"
      @onDataLoaded="onDataLoaded"
    >
      <template #filter>
        <el-select
          filterable
          clearable
          class="col-span-12 md:col-span-4"
          :placeholder="t('scanItemPlaceholder')"
          v-model="selectedItem"
          @change="onSelectedItemChanged"
          value-key="id"
          remote
          :remote-method="searchItems"
        >
          <el-option
            v-for="option in items"
            :key="option.id"
            :label="option.name"
            :value="option"
          ></el-option>
        </el-select>
      </template>
      <template #cards>
        <div class="text-xl my-2 px-2 font-bold">
          {{ selectedItem?.name }}
        </div>
        <div v-if="selectedItem" class="grid grid-cols-2 gap-x-16 gap-y-4">
          <div class="bg-white rounded-md py-2 px-4 flex items-center justify-between">
            <div class="text-gray-500">{{ t('totalSales') }}</div>
            <div class="pt-2 font-bold">{{ data?.totalSales || 0 }}</div>
          </div>
          <div class="bg-white rounded-md py-2 px-4 flex items-center justify-between">
            <div class="text-gray-500">{{ t('dateOfLastSale') }}</div>
            <div class="pt-2 font-bold">{{ data?.dateOfLastSale ? formatPostgresDate(String(data?.dateOfLastSale), 'DD/MM/YYYY'): '' }}</div>
          </div>
          <div class="bg-white rounded-md py-2 px-4 flex items-center justify-between">
            <div class="text-gray-500">{{ t('totalPurchases') }}</div>
            <div class="pt-2 font-bold">{{ data?.totalPurchases || 0 }}</div>
          </div>
          <div class="bg-white rounded-md py-2 px-4 flex items-center justify-between">
            <div class="text-gray-500">{{ t('dateOfLastPurchase') }}</div>
            <div class="pt-2 font-bold">{{ data?.dateOfLastPurchase ? formatPostgresDate(String(data?.dateOfLastPurchase), 'DD/MM/YYYY'): ''}}</div>
          </div>
          <div class="bg-white rounded-md py-2 px-4 flex items-center justify-between">
            <div class="text-gray-500">{{ t('ranking') }}</div>
            <div class="pt-2 font-bold">{{ data?.ranking || 0 }}</div>
          </div>
          <div class="bg-white rounded-md py-2 px-4 flex items-center justify-between">
            <div class="text-gray-500">{{ t('stock') }}</div>
            <div v-if="!selectedItem?.unlimitedStock" class="pt-2 font-bold">{{ calculateStock(data?.stock) }}</div>
            <div v-else class="pt-2 font-bold">{{ t('unlimited') }}</div>
          </div>
          <div class="bg-white rounded-md py-2 px-4 flex flex-col">
            <div class="text-gray-500">{{ t('topCustomers') }}</div>
            <div v-for="(row, i) in data?.topCustomers" class="flex items-center justify-between" :key="i">
              <div class="font-bold">{{ row?.customerID ? row?.name: t('cashCustomer')  }}</div>
              <div class="pt-2 font-bold">{{ row?.quantity}}</div>
            </div>
          </div>
          <div class="bg-white rounded-md py-2 px-4 flex flex-col">
            <div class="text-gray-500">{{ t('topVendors') }}</div>
            <div v-for="(row, i) in data?.topVendors" class="flex items-center justify-between" :key="i">
              <div class="font-bold">{{ row?.name }}</div>
              <div class="pt-2 font-bold">{{ row?.quantity}}</div>
            </div>
          </div>
        </div>
      </template>
      <template #chart>
        <div v-if="selectedItem" class="grid grid-cols-2 gap-2">
          <div class="col-span-12 md:col-span-2 chart-card" style="height: 400px">
            <v-chart :option="option" autoresize />
          </div>
        </div>
      </template>
    </ReportsTable>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, provide } from 'vue'
import { useI18n } from 'vue-i18n'
import { ColDef } from 'ag-grid-community'
import { useRoute, useRouter } from 'vue-router'
import { useHTTP } from '@/plugins/http'
import { useAlertModal } from '@/plugins/alert-modal/alert-modal'
import ReportsTable from '@/views/reports/v2/table/ReportsTable.vue'
import { formatPostgresDate } from '@/utils/date'
import User from '@/models/user/User'
import Item from '@/models/inventory/Item'

import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import {BarChart, LineChart} from 'echarts/charts'
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  ToolboxComponent,
  DataZoomComponent
} from 'echarts/components'
import VChart, { THEME_KEY } from 'vue-echarts'

use([
  CanvasRenderer,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  BarChart,
  LineChart,
  GridComponent,
  ToolboxComponent,
  DataZoomComponent
])

provide(THEME_KEY, 'light')

// i18n
const { t } = useI18n({
  messages: {
    en: {
      itemsInfoReport: 'Items Info Report',
      scanItemPlaceholder: 'Scan or type item',
      totalSales: 'Total Sales',
      totalVendors: 'Total Vendors',
      totalPurchases: 'Total Purchases',
      ranking: 'Ranking',
      stock: 'Stock',
      dateOfLastSale: 'Date Of last Sale',
      dateOfLastPurchase: 'Date Of last Purchase',
      topCustomers: 'Top Customers',
      topVendors: 'Top Vendors',
      cashCustomer: 'Cash Customer',
      unlimited: 'Unlimited'
    },
    ar: {
      itemsInfoReport: 'تقرير معلومات المواد',
      scanItemPlaceholder: 'مسح أو كتابة المنتج',
      totalSales: 'إجمالي المبيعات',
      totalPurchases: 'إجمالي المشتريات',
      ranking: 'التصنيف',
      stock: 'المخزون',
      dateOfLastSale: 'تاريخ آخر بيع',
      dateOfLastPurchase: 'تاريخ آخر شراء',
      topCustomers: 'أفضل العملاء',
      topVendors: 'أفضل البائعين',
      cashCustomer: 'زبون نقدي',
      unlimited: 'غير محدود'
    }
  }
})

// Data
const rtable = ref<any>()

const items = ref<Array<Item>>([])
const selectedItem = ref<Item>()
const data = ref<any>()

const http = useHTTP()
const route = useRoute()
const router = useRouter()
const alertModal = useAlertModal()

const option = ref({
  title: {
  },
  toolbox: {
    feature: {
      saveAsImage: {}
    }
  },
  tooltip: {
    trigger: 'axis',
  },
  legend: {},
  dataZoom: [
    {
      id: 'dataZoomX',
      type: 'slider',
      xAxisIndex: 0,
      filterMode: 'empty',
      start: 0,
      end: 100,
      handleSize: 0
    },
    {
      type: 'inside',
      id: 'insideX',
      xAxisIndex: 0,
      start: 0,
      end: 100,
      zoomOnMouseWheel: false,
      moveOnMouseMove: true,
      moveOnMouseWheel: true
    }
  ],
  yAxis: {
    type: 'value',
    boundaryGap: [0, 0.01],
    minInterval: 1,
    axisLabel: {
      formatter: (val: any) => (val ? val : '')
    }
  },
  xAxis: {
    type: 'category',
    data: [] as any
  },
  series: [
    {
      type: 'line',
      barWidth: 50,
      showSymbol: false,
      data: []
    }
  ]
})

onMounted(async () => {
  rtable.value.selectedPeriod= 'this_month'
  rtable.value.payload.period= 'this_month'
})

function onDataLoaded(resp: any) {
  data.value = resp
  if (data.value.salesHistory.length == 1) {
    option.value.series[0].type = 'bar'
  } else {
    option.value.series[0].type = 'line'
  }
  option.value.series[0].data = data.value.salesHistory.map(i => i.qty)
  option.value.xAxis.data = data.value.salesHistory.map(i => formatPostgresDate(i.date, "MM-DD"))
}

function onSelectedItemChanged(event: any) {
  rtable.value.payload.filter = selectedItem.value ? selectedItem.value.id : null
  rtable.value.navigate(rtable.value.payload)
}

async function searchItems(query: string) {
  if (!query) {
    query = ''
  }
  try {
    const url = `${Item.ENDPOINT}?q=${query}`
    const itemsRes = await http.get<Item[]>(url)
    items.value = itemsRes.map((item) => Item.from(item))
  } catch (error) {
    console.log(error)
  }
}

function calculateStock(stock: Array<any>) {
  let qty = 0;
  const selectedLocation = rtable.value.selectedLocation

  if (!Array.isArray(stock)) {
    return  0;
  }

  if (selectedLocation) {
    for (const s of stock) {
      if (s.locationID == selectedLocation.id) {
        return s.quantity
      }
    }

    return 0;
  }

  for (const s of stock) {
      qty += s.quantity;
  }

  return qty;
}
</script>